import * as React from 'react';
import {
  Plus,
  Loader2,
  Check,
  AlertCircle,
  Eye,
  EyeOff,
  Trash2,
  Copy,
  Settings2,
  Zap,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { getLlmConfig, saveLlmConfig, testLlmConfig } from '@/api/client';
import { AddModelModal } from '@/components/admin/add-model-modal';

type Provider = {
  name: string;
  api_base: string;
  models: string[];
  isAggregate?: boolean;
};

type ConfiguredModel = {
  id: string;
  provider: string;
  model: string;
  api_base: string;
  api_key: string;
  isDefault?: boolean;
  secondaryKey?: string; // image/video/audio/vision
  secondaryMode?: 'off' | 'auto' | 'custom';
};

const SECONDARY_TYPES = [
  { key: 'image', label: 'Image' },
  { key: 'video', label: 'Video' },
  { key: 'audio', label: 'Audio' },
  { key: 'vision', label: 'Vision' },
] as const;

export function LlmConfigTab() {
  const [providers, setProviders] = React.useState<Record<string, Provider>>({});
  const [models, setModels] = React.useState<ConfiguredModel[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [saveMsg, setSaveMsg] = React.useState('');
  const [showAdd, setShowAdd] = React.useState(false);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const data = await getLlmConfig();
      setProviders(data.providers || {});
      // 从 config 读取已保存的模型列表
      const saved = (data.config?.models as ConfiguredModel[]) || [];
      if (saved.length > 0) {
        setModels(saved);
      } else if (data.config?.provider) {
        // 兼容旧的单模型配置
        setModels([
          {
            id: 'm1',
            provider: data.config.provider,
            model: data.config.model || '',
            api_base: data.config.api_base || '',
            api_key: data.config.api_key || '',
            isDefault: true,
          },
        ]);
      } else {
        setModels([]);
      }
    } catch { /* ignore */ } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => { load(); }, [load]);

  const persist = React.useCallback(async (nextModels: ConfiguredModel[]) => {
    setModels(nextModels);
    setSaveMsg('');
    const primary = nextModels.find((m) => m.isDefault) || nextModels[0];
    setSaving(true);
    try {
      await saveLlmConfig({
        provider: primary?.provider || 'deepseek',
        model: primary?.model || '',
        api_base: primary?.api_base || '',
        api_key: primary?.api_key || '',
        models: nextModels,
      });
      setSaveMsg('已保存');
      setTimeout(() => setSaveMsg(''), 2000);
    } catch (err) {
      setSaveMsg(err instanceof Error ? err.message : '保存失败');
    } finally {
      setSaving(false);
    }
  }, []);

  const handleAdd = React.useCallback(async (m: ConfiguredModel) => {
    const next = [...models];
    if (!m.isDefault) {
      next.push(m);
    } else {
      // 设为默认：清除旧的默认标记
      const normalized = models.map((x) => ({ ...x, isDefault: false }));
      normalized.push({ ...m, isDefault: true });
      await persist(normalized);
      setShowAdd(false);
      return;
    }
    await persist(next);
    setShowAdd(false);
  }, [models, persist]);

  const handleDelete = React.useCallback(async (id: string) => {
    if (!confirm('确定删除此模型？')) return;
    const remaining = models.filter((m) => m.id !== id);
    if (remaining.length > 0 && !remaining.some((m) => m.isDefault)) {
      remaining[0].isDefault = true;
    }
    await persist(remaining);
  }, [models, persist]);

  if (loading) {
    return <Skeleton className="h-48 w-full rounded-xl" />;
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>LLM 模型设置</CardTitle>
              <CardDescription>配置 AI 改写、转录、诊断等功能的模型</CardDescription>
            </div>
            <div className="flex items-center gap-2">
              {saveMsg && (
                <span className={`inline-flex items-center gap-1 text-sm ${saveMsg === '已保存' ? 'text-emerald-500' : 'text-destructive'}`}>
                  {saveMsg === '已保存' ? <Check className="h-3.5 w-3.5" /> : <AlertCircle className="h-3.5 w-3.5" />}
                  {saveMsg}
                </span>
              )}
              <Button onClick={() => setShowAdd(true)}>
                <Plus className="h-4 w-4" />
                Add Model
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* ─── 主模型区 ─── */}
          <div>
            <h3 className="mb-3 text-sm font-medium text-muted-foreground">Primary Model</h3>
            {models.length === 0 ? (
              <div className="flex flex-col items-center justify-center rounded-lg border border-dashed py-10 text-muted-foreground">
                <Settings2 className="mb-2 h-6 w-6" />
                <p className="text-sm">尚未配置模型，点击「Add Model」添加</p>
              </div>
            ) : (
              <div className="space-y-3">
                {models.map((m) => (
                  <ModelCard
                    key={m.id}
                    model={m}
                    providerName={providers[m.provider]?.name || m.provider}
                    onDelete={() => handleDelete(m.id)}
                  />
                ))}
              </div>
            )}
          </div>

          {/* ─── 辅助模型区 ─── */}
          <div>
            <h3 className="text-sm font-medium text-muted-foreground">Secondary Models</h3>
            <p className="mb-3 mt-0.5 text-xs text-muted-foreground">
              Optional. Image / video / audio / vision models.
            </p>
            <div className="space-y-2">
              {SECONDARY_TYPES.map((type) => (
                <div key={type.key} className="flex items-center justify-between rounded-lg border px-4 py-2.5">
                  <span className="text-sm font-medium">{type.label}</span>
                  <div className="flex items-center gap-1">
                    <ModeButton label="Off" />
                    <ModeButton label="Auto" disabled />
                    <ModeButton label="Custom" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ─── Add Model 弹窗 ─── */}
      {showAdd && (
        <AddModelModal
          providers={providers}
          onCancel={() => setShowAdd(false)}
          onSave={handleAdd}
        />
      )}

      {saving && (
        <div className="fixed bottom-4 right-4 flex items-center gap-2 rounded-full bg-primary px-4 py-2 text-sm text-primary-foreground shadow-lg">
          <Loader2 className="h-4 w-4 animate-spin" />
          保存中…
        </div>
      )}
    </div>
  );
}

// ─── 主模型卡片 ────────────────────────────────────────────────

function ModelCard({
  model,
  providerName,
  onDelete,
}: {
  model: ConfiguredModel;
  providerName: string;
  onDelete: () => void;
}) {
  const [testing, setTesting] = React.useState(false);
  const [testResult, setTestResult] = React.useState<{ ok: boolean; msg: string } | null>(null);
  const [showKey, setShowKey] = React.useState(false);

  const maskedKey = model.api_key
    ? model.api_key.length > 12
      ? `${model.api_key.slice(0, 6)}****${model.api_key.slice(-4)}`
      : `****${model.api_key.slice(-4)}`
    : '未设置 Key';

  const handleTest = React.useCallback(async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const data = await testLlmConfig(model.id);
      setTestResult({ ok: data.ok, msg: data.message });
    } catch (error) {
      setTestResult({ ok: false, msg: error instanceof Error ? error.message : '连接失败' });
    } finally {
      setTesting(false);
    }
  }, [model]);

  return (
    <div className="rounded-lg border p-4 transition-colors hover:bg-accent/20">
      <div className="flex items-start justify-between gap-3">
        {/* 左侧信息 */}
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-semibold">{model.model || '未命名模型'}</span>
            {model.isDefault && <Badge variant="default">默认</Badge>}
          </div>
          <div className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground">
            <span>{providerName}</span>
            <span className="inline-flex items-center gap-1 font-mono text-xs">
              {showKey ? model.api_key || '—' : maskedKey}
              <button
                onClick={() => setShowKey((v) => !v)}
                className="text-muted-foreground hover:text-foreground"
                title={showKey ? '隐藏 Key' : '显示 Key'}
              >
                {showKey ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
              </button>
            </span>
            {model.api_base && (
              <span className="max-w-[220px] truncate text-xs font-mono text-muted-foreground/70">{model.api_base}</span>
            )}
          </div>
        </div>

        {/* 右侧操作 */}
        <div className="flex flex-shrink-0 items-center gap-1">
          <Button variant="outline" size="sm" className="h-8 text-xs" onClick={handleTest} disabled={testing}>
            {testing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Zap className="h-3.5 w-3.5" />}
            Test
          </Button>
          <Button variant="ghost" size="icon" className="h-8 w-8" title="复制" onClick={() => navigator.clipboard.writeText(`${model.provider}/${model.model}`)}>
            <Copy className="h-3.5 w-3.5" />
          </Button>
          <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-destructive" title="删除" onClick={onDelete}>
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>
      {testResult && (
        <div className={`mt-2 flex items-center gap-1.5 text-xs ${testResult.ok ? 'text-emerald-500' : 'text-destructive'}`}>
          {testResult.ok ? <Check className="h-3.5 w-3.5" /> : <AlertCircle className="h-3.5 w-3.5" />}
          {testResult.msg}
        </div>
      )}
    </div>
  );
}

// ─── 辅助模型 Off/Auto/Custom 按钮 ─────────────────────────────

function ModeButton({ label, disabled }: { label: string; disabled?: boolean }) {
  return (
    <button
      disabled={disabled}
      className={`rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
        disabled
          ? 'cursor-not-allowed text-muted-foreground/40'
          : 'bg-secondary text-secondary-foreground hover:bg-accent'
      }`}
    >
      {label}
    </button>
  );
}